import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MonoLabel } from '../components/Editorial';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import { clientProofHeaders, clientProofMessage } from '../lib/clientProof';

const AuthShell = ({ children, headline, quote }) => (
  <div className="ed-auth">
    <div className="ed-auth__hero">
      <div>
        <MonoLabel onDark>GUARDIAN INVITE</MonoLabel>
        <h1 className="ed-auth__hero-headline" style={{ marginTop: 16 }}>
          {headline}
        </h1>
      </div>
      <p className="ed-auth__hero-quote">{quote}</p>
    </div>
    <div className="ed-auth__form-wrap">
      <div className="ed-auth__form">{children}</div>
    </div>
  </div>
);

/**
 * Web target for the guardian-invite email's Accept link.
 * URL: `/guardian/accept?token=<single-use token>`
 *
 * Flow:
 *  - Load page, read token from query string.
 *  - GET /api/guardians/preview-invite to fetch athlete name + relation
 *    WITHOUT consuming the token. Lets us address the parent by name and
 *    explain what they're agreeing to before they act.
 *  - POST /api/guardians/accept on submit. Backend creates the guardian
 *    user inline if no account at the invited email; otherwise links the
 *    existing user. Either way returns a LoginResponse-shaped payload.
 *  - On success, persist tokens like AuthContext.login() does and full-
 *    reload to /dashboard so AuthProvider picks up the new session.
 */
export default function GuardianAcceptPage() {
  const [searchParams] = useSearchParams();
  useAuth();
  const token = searchParams.get('token');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /** Invite preview — null while loading, an object once fetched, or an
   *  error string when the token is invalid/expired. */
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(true);

  useEffect(() => {
    if (!token) { setPreviewLoading(false); return; }
    let cancelled = false;
    axios.get('/api/guardians/preview-invite', { params: { token } })
      .then(({ data }) => { if (!cancelled) { setPreview(data); setPreviewLoading(false); } })
      .catch((err) => {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        setPreviewError(typeof detail === 'string' ? detail : 'Could not load this invite.');
        setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // Phone is required for new guardians (we need to be able to reach the
    // parent if a coach connects with their athlete). Returning users
    // (hasAccount === true) already have a phone on file, so we only
    // require it on the new-account branch.
    if (showNameFields && !phone.trim()) {
      setError('Phone number is required.');
      setLoading(false);
      return;
    }

    try {
      const body = { token };
      if (firstName.trim()) body.first_name = firstName.trim();
      if (lastName.trim()) body.last_name = lastName.trim();
      if (phone.trim()) body.phone = phone.trim();
      if (password) body.password = password;

      const { data } = await axios.post('/api/guardians/accept', body,
        { headers: await clientProofHeaders('/api', 'claim') });

      if (data.access_token) {
        localStorage.setItem('token', data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      if (data.access_token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`;
      }
      // New guardian accounts get a /welcome step (parent profile + Pro
      // upgrade CTA) before being dropped on the athlete-centric dashboard.
      // Returning users skip straight through.
      if (data.is_new_user) {
        const params = new URLSearchParams({
          athlete_id: data.athlete_user_id ?? '',
          athlete_first: data.athlete_first_name ?? '',
          athlete_premium: data.athlete_premium_active ? '1' : '0',
        });
        window.location.assign(`/welcome?${params.toString()}`);
      } else {
        window.location.assign('/dashboard');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const proofMessage = clientProofMessage(err);
      if (proofMessage) {
        setError(proofMessage);
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Could not accept invite. The link may be invalid or expired.');
      }
    }
    setLoading(false);
  };

  // No token in URL.
  if (!token) {
    return (
      <AuthShell
        headline={<>Accept your<br /><em>invite.</em></>}
        quote="Manage an athlete's profile, message coaches on their behalf, and pay their subscription."
      >
        <div className="auth-logo" style={{ marginBottom: 24 }}>
          <img src="/logo.png" alt="ProZpects" style={{ maxHeight: 36 }} />
        </div>
        <div className="auth-error" role="alert">
          Invalid invite link — no token provided.
        </div>
        <div className="auth-footer">
          <Link to="/login">Back to login</Link>
        </div>
      </AuthShell>
    );
  }

  // Token preview failed (invalid / revoked / expired / already accepted).
  if (previewError) {
    return (
      <AuthShell
        headline={<>Accept your<br /><em>invite.</em></>}
        quote="Manage an athlete's profile, message coaches on their behalf, and pay their subscription."
      >
        <div className="auth-logo" style={{ marginBottom: 24 }}>
          <img src="/logo.png" alt="ProZpects" style={{ maxHeight: 36 }} />
        </div>
        <div className="auth-error" role="alert">{previewError}</div>
        <div className="auth-footer">
          <Link to="/login">Back to login</Link>
        </div>
      </AuthShell>
    );
  }

  const athleteFirst = preview?.athlete_first_name || '';
  const athleteFull  = [preview?.athlete_first_name, preview?.athlete_last_name].filter(Boolean).join(' ');
  const relation     = (preview?.relation || 'guardian').toLowerCase();
  const hasAccount   = !!preview?.has_existing_account;

  // Hero copy keyed off the athlete's first name when known.
  const heroHeadline = athleteFirst
    ? <>Manage <em>{athleteFirst}'s</em><br />recruiting.</>
    : <>Accept your<br /><em>invite.</em></>;

  const heroQuote = athleteFull
    ? `${athleteFull} invited you as a ${relation} to manage their ProZpects profile, message coaches, and handle billing.`
    : "Manage an athlete's profile, message coaches on their behalf, and pay their subscription.";

  // Body copy: returning user vs new user.
  const introCopy = hasAccount
    ? `Sign in to link your account to ${athleteFirst || 'this athlete'}.`
    : `Pick a password to create your guardian account. We'll link you to ${athleteFirst || 'the athlete'} as soon as you accept.`;

  // Returning users only need the password field; new users need name too.
  const showNameFields = !hasAccount;

  return (
    <AuthShell headline={heroHeadline} quote={heroQuote}>
      <div className="auth-logo" style={{ marginBottom: 24 }}>
        <img src="/logo.png" alt="ProZpects" style={{ maxHeight: 36 }} />
      </div>
      <MonoLabel>{hasAccount ? 'WELCOME BACK' : 'ACCEPT INVITE'}</MonoLabel>
      <h2 style={{ fontFamily: 'var(--ed-font-serif)', fontSize: 36, fontWeight: 400, letterSpacing: '-0.015em', margin: '8px 0 16px' }}>
        {hasAccount ? <>Hi, {relation}.</> : <>Welcome.</>}
      </h2>

      <p style={{ fontSize: 14, color: 'var(--ed-color-muted)', marginBottom: 20, lineHeight: 1.5 }}>
        {introCopy}
      </p>

      {previewLoading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--ed-color-muted)' }}>Loading invite…</div>
      ) : (
        <form className="auth-form" onSubmit={handleSubmit}>
          {error && (
            <div className="auth-error" role="alert">{error}</div>
          )}

          {showNameFields && (
            <>
              <div className="auth-field">
                <label htmlFor="accept-first">First name</label>
                <input
                  id="accept-first"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>

              <div className="auth-field">
                <label htmlFor="accept-last">Last name</label>
                <input
                  id="accept-last"
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>

              <div className="auth-field">
                <label htmlFor="accept-phone">Phone</label>
                <input
                  id="accept-phone"
                  type="tel"
                  autoComplete="tel"
                  placeholder="(555) 555-5555"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </div>
            </>
          )}

          <div className="auth-field">
            <label htmlFor="accept-password">{hasAccount ? 'Password' : 'Pick a password'}</label>
            <div className="auth-password-wrapper">
              <input
                id="accept-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete={hasAccount ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="auth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </Button>
            </div>
            {!hasAccount && (
              <div className="auth-password-hint">
                8+ characters with uppercase, lowercase, and a special character (!@#$%^&*).
              </div>
            )}
          </div>

          <Button type="submit" variant="primary" className="auth-btn" disabled={loading}>
            {loading
              ? 'Accepting…'
              : hasAccount
                ? `Sign in & link to ${athleteFirst || 'athlete'}`
                : 'Accept invite'}
          </Button>
        </form>
      )}

      <div className="auth-footer">
        <Link to="/login">Back to login</Link>
      </div>
    </AuthShell>
  );
}
